/* global G */
import { asyncPipeSpread, asyncPipeSpreadIf, curry } from 'lib/util'
import { hasHouseNumber } from 'app/_shared/events/address/index'
import init from 'app/_shared/events/address/partials/init'
import displayHouseNumberRow from 'app/_shared/events/address/partials/displayRow'
import { get } from 'lib/sequence/component/state/value'

/**
 * Returns whether the house number field should be displayed for `country`.
 *
 * @param {string} country the selected country
 * @returns {boolean}
 */
const shouldDisplayHouseNumber = country => !!(country && hasHouseNumber(country))

/**
 * Shows a toast that the previously set house number has been clear if a house number was
 * set before, and we're not executing this on first render (initAddress should be set).
 *
 * @param {Object} data
 * @param {Gaia.Component.Spec} data.primary    the primary component
 * @param {Gaia.Component.Spec} data.secondary  the secondary component
 * @param {ComponentInfo}       data.c          information about the component to use
 * @param {Gaia.AppModule.Spec} module          the current module composition object
 * @param {*[]} args                            residual args
 * @returns {*[]}
 */
const showToast = (data, module, ...args) => {
  const { primary, c } = data
  const actionState = module[G.STATE][G.ACTION][G.STATE]
  const { [G.EVENTS]: eventBus, [G.INTL]: intl } = module[G.ADAPTER]

  const isInitialized = actionState?.[c.parent || c.component]?.initAddress

  if (isInitialized && get(primary)) {
    eventBus.dispatch(eventBus.type(G.DATA, G.UPDATE),
      {
        message: intl._t(
          'data.houseNumberDeleted',
          {
            ns: 'common',
            _key: 'data.houseNumberDeleted',
            defaultValue: 'The previous street number has been cleared',
          },
        ),
        portal: true,
        severity: 'info',
        action: false,
        close: 3000,
      })
  }

  return [data, module, ...args]
}

/**
 * Manage House Number Event Handler
 *
 * Controls the visual appearance of the street and house number fields in an address
 * form depending on the selected country.
 *
 * The `primary` field is the field that is actively mutated (shown/hidden) by this handler.
 * The `secondary` field is the field that is passively mutated (stretched/narrowed) by this
 * handler in depending on the state of the primary field.
 *
 * @param {string} primary      the primary field
 * @param {string} secondary    the secondary field
 * @param {ComponentInfo} c     information about what component to use
 *
 * @returns {(function(...[*]): (*))|*}
 */
const displayHouseNumber = (primary, secondary, c) => (module, component, event) => asyncPipeSpread(
  init(primary, secondary, c),
  asyncPipeSpreadIf(({ value }) => shouldDisplayHouseNumber(value))(
    displayHouseNumberRow(false),
  ),
  asyncPipeSpreadIf(({ value }) => !shouldDisplayHouseNumber(value))(
    showToast,
    displayHouseNumberRow(true),
  ),
)(module, component, event)

export default curry(displayHouseNumber)
